'use client';

import React from 'react';
import { Question } from '../../types';
import { QuizItem } from '@/store/assessment-store';
import { AnswerHandlers } from '../../hooks/useAnswerHandlers';
import { OptionCorrectnessChecker } from '../interfaces';
import { MultipleChoiceOptionsInteractive, MultipleChoiceOptionsReview } from './MultipleChoiceOptions';
import { SingleChoiceOptionsInteractive, SingleChoiceOptionsReview } from './SingleChoiceOptions';
import { TrueFalseOptionsInteractive, TrueFalseOptionsReview } from './TrueFalseOptions';

/**
 * Factory pattern for creating option renderers
 * Follows Open/Closed Principle - open for extension, closed for modification
 * Follows Dependency Inversion Principle - depends on abstractions (interfaces)
 */

interface BaseOptionRendererProps {
  question: Question;
  questionType: string;
  questionIndex: number;
  selectedOptions: string | string[] | boolean | undefined;
}

interface InteractiveRendererProps extends BaseOptionRendererProps {
  quiz: QuizItem[];
  handlers: AnswerHandlers;
  isQuiz?: boolean;
  correctAnswers?: string | string[] | boolean | null;
  quizData?: any;
  isQuestionSubmitted?: boolean;
}

interface ReviewRendererProps extends BaseOptionRendererProps {
  showCorrectness: boolean;
  correctnessChecker?: OptionCorrectnessChecker['check'];
  correctAnswers?: string | string[] | boolean | null;
}

/**
 * Render options for interactive mode (taking assessment)
 */
export function renderInteractiveOptions({
  question,
  questionType,
  questionIndex,
  selectedOptions,
  quiz,
  handlers,
  isQuiz = false,
  correctAnswers = null,
  quizData,
  isQuestionSubmitted = false,
}: InteractiveRendererProps): React.ReactNode {
  const baseProps = {
    question,
    questionIndex,
    selectedOptions,
    isQuiz,
    correctAnswers,
    quizData,
    isQuestionSubmitted,
  };

  switch (questionType) {
    case 'multiple_choice_question':
      return (
        <MultipleChoiceOptionsInteractive
          {...baseProps}
          handlers={handlers}
        />
      );
    case 'single_choice_question':
      return (
        <SingleChoiceOptionsInteractive
          {...baseProps}
          handlers={handlers}
        />
      );
    case 'true_or_false_question':
      return (
        <TrueFalseOptionsInteractive
          {...baseProps}
          handlers={handlers}
        />
      );
    default:
      return null;
  }
}

/**
 * Render options for review mode (showing results)
 */
export function renderReviewOptions({
  question,
  questionType,
  questionIndex,
  selectedOptions,
  showCorrectness,
  correctnessChecker,
  correctAnswers = null,
}: ReviewRendererProps): React.ReactNode {
  const baseProps = {
    question,
    questionIndex,
    selectedOptions,
    showCorrectness,
    correctnessChecker,
    correctAnswers,
  };

  switch (questionType) {
    case 'multiple_choice_question':
      return <MultipleChoiceOptionsReview {...baseProps} />;
    case 'single_choice_question':
      return <SingleChoiceOptionsReview {...baseProps} />;
    case 'true_or_false_question':
      return <TrueFalseOptionsReview {...baseProps} />;
    default:
      return null;
  }
}

